import { useEffect } from 'react';
import { socketService } from '@/services/SocketService';

/** What the server's `{agent_type}:session_ready` told us: which session to vouch for and how often. */
export interface VoiceKeepalive {
  sessionId: string;
  intervalSeconds: number;
}

/** Read `keepalive_interval_seconds` from a `session_ready` payload. Null when the server sent none
 *  (an older api-service with no silence sweep) — then no keepalive is needed. */
export function keepaliveFromSessionReady(sessionId: string, data: unknown): VoiceKeepalive | null {
  const interval = (data as { keepalive_interval_seconds?: unknown } | null)?.keepalive_interval_seconds;
  return typeof interval === 'number' && interval > 0 ? { sessionId, intervalSeconds: interval } : null;
}

/** While a live voice session is open, tell api-service the client is still here every
 *  `intervalSeconds` (`{agentType}.keepalive`) — including while the mic is muted, when no audio
 *  flows. api-service ends a session that goes silent past chat_settings.voice_client_silence_timeout
 *  _seconds: that is what closes the paid realtime connection after a closed tab or reload whose
 *  disconnect was never noticed. Pass null to stop. */
export function useVoiceKeepalive(agentType: string, keepalive: VoiceKeepalive | null): void {
  const sessionId = keepalive?.sessionId;
  const intervalSeconds = keepalive?.intervalSeconds;
  useEffect(() => {
    if (!sessionId || !intervalSeconds) return undefined;
    const id = window.setInterval(() => {
      socketService.emit('message', { type: `${agentType}.keepalive`, session_id: sessionId });
    }, intervalSeconds * 1000);
    return () => window.clearInterval(id);
  }, [agentType, sessionId, intervalSeconds]);
}
